import { Inject, Injectable } from "@angular/core";
import { CoursesService } from "@carnegie-learning/data-access-angular-passport";
import { Observable, of } from "rxjs";
import { catchError, map } from "rxjs/operators";
import {
    ACTIVITY_ID_STORAGE_KEY,
    CLASS_ID_STORAGE_KEY,
    INTENT_PERFORM,
    INTENT_PICK,
    INTENT_STORAGE_KEY,
    LAUNCH_ID_STORAGE_KEY,
    SCHOOL_ID_STORAGE_KEY,
    LaunchIntent,
    PLAYER_STORAGE_KEY,
} from "#core/constants/intent-mode.constants";
import { Course } from "cli-types";
import { ASSETS_SERVICE } from "@carnegie-learning/data-access-angular-cure";
import { AssetsService } from "@carnegie-learning/data-access-cure";

@Injectable({ providedIn: "root" })
export class LaunchIntentService {
    constructor(
        private readonly coursesService: CoursesService, 
        @Inject(ASSETS_SERVICE) private assetsService: AssetsService,
    ) {}

    persistFromLaunchContext(launchContext: any): void {
        const intent = this.normalizeIntent(
            launchContext?.intent ?? launchContext?.applicationContext?.intent
        );
        const playerId = launchContext?.applicationContext?.playerId ?? null;
        // FOOTGUN: applicationContext.classId is the course GUID (e.g.
        // "cl_74ud8389799552"), NOT the numeric passport courseID used in routes/APIs.
        // See getClassId() below — it must be resolved before use as a route param.
        const classId = launchContext?.applicationContext?.classId ?? null;
        const activityId = launchContext?.applicationContext?.assetId ?? null;
        const launchId =
            launchContext?.applicationContext?.launchId ?? launchContext?.launchId ?? null;
        const schoolId = launchContext?.schoolId ?? null;

        this.setStorage(INTENT_STORAGE_KEY, intent);
        this.setStorage(PLAYER_STORAGE_KEY, playerId);
        this.setStorage(CLASS_ID_STORAGE_KEY, classId);
        this.setStorage(ACTIVITY_ID_STORAGE_KEY, activityId);
        this.setStorage(LAUNCH_ID_STORAGE_KEY, launchId);
        this.setStorage(SCHOOL_ID_STORAGE_KEY, schoolId);
    }

    getIntent(): LaunchIntent | null {
        return this.normalizeIntent(this.getStorage(INTENT_STORAGE_KEY));
    }

    getPlayerId(): string | null {
        return this.getStorage(PLAYER_STORAGE_KEY);
    }

    /**
     * FOOTGUN — READ BEFORE USING:
     * This returns the LMS course **GUID** (e.g. "cl_74ud8389799552"), the value rex
     * puts in `applicationContext.classId`. It is NOT the numeric passport `courseID`
     * that the app's routes (`/student/courses/:courseID/...`) and the assignments API
     * (`courseID=`) expect. `Number(getClassId())` is NaN, and using it as a route param
     * produces an invalid `/student/courses/<GUID>/assignments` URL.
     *
     * To get the numeric courseID, resolve this GUID against the student's courses:
     *   courses.find(c => c.courseGUID === launchIntentService.getClassId())?.courseID
     * (see StudentLcRedirectGuard and AssignmentsEffects.lookupAssignmentByAsset$).
     */
    getClassId(): string | null {
        return this.getStorage(CLASS_ID_STORAGE_KEY);
    }

    getCourse(): Observable<Course | null> {
        const classId = this.getClassId();
        if (!classId) {
            return of(null);
        }
        return this.coursesService.getCourse(classId).pipe(
            map((course) => {
                if (course) {
                    return course
                }
                return null;
            }),
            catchError(() => of(null))
        );
    }

    getActivityId(): string | null {
        return this.getStorage(ACTIVITY_ID_STORAGE_KEY);
    }

    getFullAsset() {
        const assetId = this.getActivityId();
        if (!assetId) {
            return of(null);
        }
        return this.assetsService.getSelectedAsset(assetId).pipe(
            map((asset) => {
                if (asset) {
                    return asset;
                }
                return null;
            }),
            catchError(() => of(null))
        );
    }

    getLaunchId(): string | null {
        return this.getStorage(LAUNCH_ID_STORAGE_KEY);
    }

    getSchoolId(): string | null {
        return this.getStorage(SCHOOL_ID_STORAGE_KEY);
    }

    clearIntent(): void {
        this.setStorage(INTENT_STORAGE_KEY, null);
        this.setStorage(PLAYER_STORAGE_KEY, null);
        this.setStorage(CLASS_ID_STORAGE_KEY, null);
        this.setStorage(ACTIVITY_ID_STORAGE_KEY, null);
        this.setStorage(LAUNCH_ID_STORAGE_KEY, null);
        this.setStorage(SCHOOL_ID_STORAGE_KEY, null);
    }

    isIntentMode(): boolean {
        return !!this.getIntent();
    }

    isTeacherPick(): boolean {
        return this.getIntent() === INTENT_PICK && this.getPlayerId() === "teacher";
    }

    isTeacherPerform(): boolean {
        return (
            this.getIntent() === INTENT_PERFORM && this.getPlayerId() === "teacher"
        );
    }

    isStudentPerform(): boolean {
        return (
            this.getIntent() === INTENT_PERFORM && this.getPlayerId() === "student"
        );
    }

    private normalizeIntent(intent: any): LaunchIntent | null {
        if (typeof intent !== "string") {
            return null;
        }
        const upperIntent = intent.toUpperCase();
        if (upperIntent === INTENT_PICK || upperIntent === INTENT_PERFORM) {
            return upperIntent;
        }
        return null;
    }

    private setStorage(key: string, value: string | null): void {
        if (globalThis.window === undefined) {
            return;
        }
        if (value === null || value === "") {
            globalThis.sessionStorage.removeItem(key);
            return;
        }
        globalThis.sessionStorage.setItem(key, value);
    }

    /**
     * Report a completion score back to the originating LMS via the LTI 1.3 mediator.
     * @param score Normalised score in [0, 1]. Pass 1.0 for full completion.
     *
     * TODO: Confirm the exact FIDO LTI 1.3 mediator endpoint and payload shape with the
     *       backend team, then replace this stub with a real HttpClient POST to
     *       `apiHost + "/api/lti/score/" + launchId`.
     *       Config key: `cl.services.fido.lti.mediator.rootUrl`
     */
    reportScore(score: number): void {
        const launchId = this.getLaunchId();
        if (!launchId) {
            return;
        }
        // Stub — backend endpoint not yet confirmed.
        console.warn('[LTI intent] reportScore stub — launchId:', launchId, 'score:', score);
    }

    private getStorage(key: string): string | null {
        if (globalThis.window === undefined) {
            return null;
        }
        return globalThis.sessionStorage.getItem(key);
    }
}
